import { useFormik } from "formik";
import * as Yup from "yup";

import { useMutation, useQuery } from "@tanstack/react-query";
import SettingsHeader from "./CreateEditSettingsHeader";
import UpdateSettingsForm from "./UpdateSettingsForm";
import CompanyDetailsView from "./CompanyDetailsView";
import companyFormFields from "./CompanyFormFields";
import { getCompanyUserById, updateCompany } from "../../../../services/api/company";
import useAuth from "../../../../hooks/useAuth";
import { showToast } from "../../../../store/toast/toastSlice";
import { useDispatch } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import BaseLoader from "../../../../components/ui/BaseLoader";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseButton from "../../../../components/ui/BaseButton";
import { me } from "../../../../services/api/user";
import { UserProfileStatus } from "../../../../utils/enum";

const SECTIONS = {
  logo: {
    title: "Company Logo",
    fields: ["logo"],
  },
  information: {
    title: "Company Information",
    fields: [
      "name",
      "email",
      "website",
      "phone",
      "fax",
      "annualRevenue",
      "searchAddress",
      "address",
      "city",
      "state",
      "zipCode",
      "country",
    ],
  },
  registration: {
    title: "Registration",
    fields: ["registrationNumber", "vatNumber", "registrationCertificate"],
  },
  insurance: {
    title: "Insurance",
    fields: ["publicLiability", "workerCompensation"],
  },
};

const validationSchema = Yup.object({
  name: Yup.string().notRequired(),
  email: Yup.string()
    .notRequired()
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
  website: Yup.string().notRequired(),
  mobileNumber: Yup.string().notRequired(),
  phoneNumber: Yup.string().notRequired(),
  fax: Yup.string().notRequired(),
  companyAnnualRevenue: Yup.string().notRequired(),
  address: Yup.string().notRequired(),
  state: Yup.string().notRequired(),
  country: Yup.string().notRequired(),
  zipCode: Yup.string().notRequired(),
  logo: Yup.mixed().nullable().notRequired(),
  registrationNumber: Yup.string().notRequired(),
  vatNumber: Yup.string().notRequired(),
  registrationCertificate: Yup.mixed().nullable().notRequired(),
  publicLiability: Yup.mixed().nullable().notRequired(),
  workerCompensation: Yup.mixed().nullable().notRequired(),
});

const UpdateSettings = ({
  completeProfile = false,
  setIsAllowToSubmit = null,
  overrideCompanyId = null,
  isFreelanceCompany = false,
  onDirtyChange = null,
  onCompletionChange = null,
  readOnly = false,
  user = null,
}) => {
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const companyId = overrideCompanyId ?? user?.company?.id ?? auth?.user?.company?.id;
  const [isEditable, setIsEditable] = useState(false);
  // The SECTIONS key whose modal is open on the settings page.
  const [editingSection, setEditingSection] = useState(null);

  const {
    isPending: isFetchCompanySettingsLoading,
    data: companySettingsData,
    refetch: refetchCompanySettings,
  } = useQuery({
    queryKey: ["companySettings", companyId],
    queryFn: () => getCompanyUserById(companyId),
    enabled: !!companyId,
  });

  const { isPending: isUpdateCompanyApiLoading, mutateAsync: updateCompanyMutation } = useMutation({
    mutationFn: ([id, payload]) => updateCompany(id, payload),
  });

  const { data: userData } = useQuery({ queryKey: ["userData"], queryFn: me, retry: 2 });
  // Registration is what the approval was based on, so once approved the company itself
  // can no longer change it. `user` is set only when someone else's company is open.
  const isRegistrationLocked =
    isFreelanceCompany &&
    !user &&
    [UserProfileStatus.APPROVED, UserProfileStatus.ACTIVATED].includes(userData?.status);

  const selectedCompanySettings = companySettingsData?.data;

  const handleClientCreateOrUpdateButton = async (values) => {
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("website", values.website);
      formData.append("mobileNumber", values.mobileNumber);
      formData.append("phoneNumber", values.phoneNumber || "");
      formData.append("fax", values.fax);
      formData.append("companyAnnualRevenue", values.companyAnnualRevenue);
      formData.append("address", values.address);
      formData.append("state", values.state);
      formData.append("country", values.country);
      formData.append("city", values.city);
      formData.append("zipCode", values.zipCode);
      formData.append("latitude", values.latitude);
      formData.append("longitude", values.longitude);
      formData.append("registrationNumber", values.registrationNumber);
      formData.append("vatNumber", values.vatNumber);

      if (values.logo) formData.append("companyLogo", values.logo);
      if (values.registrationCertificate)
        formData.append("registrationCertificate", values.registrationCertificate);
      if (values.publicLiability)
        formData.append("publicLiabilityInsurance", values.publicLiability);
      if (values.workerCompensation)
        formData.append("workerCompensationInsurance", values.workerCompensation);

      await updateCompanyMutation([companyId, formData]);

      dispatch(
        showToast({
          message: "Company updated successfully",
          type: "success",
        }),
      );
      refetchCompanySettings();
      setIsEditable(false);
      setEditingSection(null);
      if (setIsAllowToSubmit) setIsAllowToSubmit(checkProfileCanSubmit(values));
    } catch (error) {
      console.error("❌ Company update failed:", error.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: selectedCompanySettings?.name || "",
      email: selectedCompanySettings?.email || "",
      website: selectedCompanySettings?.website || "",
      mobileNumber: selectedCompanySettings?.mobileNumber || "",
      phoneNumber: selectedCompanySettings?.phoneNumber || "",
      fax: selectedCompanySettings?.fax || "",
      companyAnnualRevenue: selectedCompanySettings?.companyAnnualRevenue || "",
      address: selectedCompanySettings?.address?.addressLine1 || "",
      city: selectedCompanySettings?.address?.city || "",
      latitude: selectedCompanySettings?.address?.latitude || "",
      longitude: selectedCompanySettings?.address?.longitude || "",
      state: selectedCompanySettings?.address?.state || "",
      country: selectedCompanySettings?.address?.country || "",
      zipCode: selectedCompanySettings?.address?.zipCode || "",
      logo: selectedCompanySettings?.digitalAssets?.COMPANY_LOGO?.[0] || null,
      registrationNumber: selectedCompanySettings?.registrationNumber || "",
      vatNumber: selectedCompanySettings?.vatNumber || "",
      registrationCertificate:
        selectedCompanySettings?.digitalAssets?.REGISTRATION_CERTIFICATE?.[0] || null,
      publicLiability:
        selectedCompanySettings?.digitalAssets?.PUBLIC_LIABILITY_INSURANCE?.[0] || null,
      workerCompensation:
        selectedCompanySettings?.digitalAssets?.WORKER_COMPENSATION_INSURANCE?.[0] || null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleClientCreateOrUpdateButton,
  });
  const checkProfileCanSubmit = (company) => {
    const hasRegistrationNumber = !!company?.registrationNumber;
    const hasAddress = !!company?.address?.addressLine1 ?? !!company?.address;
    const hasRegistrationCertificate = !!company?.digitalAssets?.REGISTRATION_CERTIFICATE?.length;
    const hasAnnualRevenue = isFreelanceCompany
      ? !!company?.companyAnnualRevenue && company.companyAnnualRevenue !== "N/A"
      : true;

    return hasRegistrationNumber && hasAddress && hasRegistrationCertificate && hasAnnualRevenue;
  };

  // The still-missing required fields, used to drive the wizard step's tooltip.
  const companyMissingFields = (company) => {
    const missing = [];
    if (!company?.registrationNumber) missing.push("Registration number");
    if (!(company?.address?.addressLine1 ?? company?.address)) missing.push("Company address");
    if (!company?.digitalAssets?.REGISTRATION_CERTIFICATE?.length)
      missing.push("Registration certificate");
    if (
      isFreelanceCompany &&
      (!company?.companyAnnualRevenue || company.companyAnnualRevenue === "N/A")
    )
      missing.push("Annual revenue");
    return missing;
  };

  useEffect(() => {
    if (!completeProfile) return;
    setIsAllowToSubmit(checkProfileCanSubmit(selectedCompanySettings));
    onCompletionChange?.(companyMissingFields(selectedCompanySettings));
  }, [completeProfile, selectedCompanySettings]);

  // Report unsaved edits up so the side-nav step can prompt the user to Save.
  useEffect(() => {
    onDirtyChange?.(formik.dirty);
  }, [formik.dirty]);

  const hasAnyCompanyData = (company) => {
    if (!company) return false;

    return (
      company.registrationNumber ||
      company.vatNumber ||
      company.address?.addressLine1 ||
      company.address?.state ||
      company.address?.country ||
      company.address?.zipCode ||
      company.digitalAssets?.REGISTRATION_CERTIFICATE?.length ||
      company.digitalAssets?.PUBLIC_LIABILITY_INSURANCE?.length ||
      company.digitalAssets?.WORKER_COMPENSATION_INSURANCE?.length
    );
  };
  const hasData = hasAnyCompanyData(selectedCompanySettings);
  useEffect(() => {
    if (!selectedCompanySettings) return;

    // A submitted profile stays as it was sent, even when sections are still empty.
    if (!hasAnyCompanyData(selectedCompanySettings)) {
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
  }, [selectedCompanySettings]);

  if (isFetchCompanySettingsLoading || !selectedCompanySettings) {
    return (
      <div className="p-6">
        <BaseLoader isLoading={isFetchCompanySettingsLoading} />
      </div>
    );
  }
  if (!completeProfile) {
    const section = SECTIONS[editingSection];
    const fields = companyFormFields(formik, { isFreelanceCompany });
    const closeModal = () => {
      formik.resetForm();
      setEditingSection(null);
    };

    return (
      <>
        <CompanyDetailsView
          values={formik.initialValues}
          onEdit={readOnly ? null : setEditingSection}
          isRegistrationLocked={!readOnly && isRegistrationLocked}
        />
        {section && (
          <BaseModal
            className="-top-[200px] overflow-auto"
            open
            close={closeModal}
            style={{ width: "1060px" }}
          >
            <div className="py-4 px-8">
              <div className="flex justify-between items-center border-b-2 pb-1">
                <h5 className="text-dark font-normal">{section.title}</h5>
              </div>
            </div>

            <div className="px-8 pb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 md:gap-8">
                {section.fields.map((key) => (
                  <Fragment key={key}>{fields[key]}</Fragment>
                ))}
              </div>
            </div>

            <div
              className="d-flex content-center mt-10 bg-white py-6"
              style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
            >
              <BaseButton
                variant="outlined"
                color="primary"
                className="px-[28px] py-[5px] bg-light text-primary hover:text-primary"
                style={{ marginRight: "16px" }}
                disabled={isUpdateCompanyApiLoading}
                onClick={closeModal}
              >
                Cancel
              </BaseButton>
              <BaseButton
                loaderSize="22px"
                isLoading={isUpdateCompanyApiLoading}
                color="primary"
                className="py-[5px] px-[38px]"
                disabled={!formik.dirty || isUpdateCompanyApiLoading}
                onClick={formik.handleSubmit}
              >
                Save
              </BaseButton>
            </div>
          </BaseModal>
        )}
      </>
    );
  }

  return (
    <div>
      <SettingsHeader
        onSave={formik.handleSubmit}
        isLoading={isUpdateCompanyApiLoading}
        completeProfile={completeProfile}
        isUpdated={formik.dirty}
        isEditable={isEditable}
        setIsEditable={setIsEditable}
        onCancel={() => {
          formik.resetForm();
          setIsEditable(false);
        }}
        hasData={hasData}
      />
      <UpdateSettingsForm
        formik={formik}
        isEditable={isEditable}
        completeProfile={completeProfile}
        hasData={hasData}
        isFreelanceCompany={isFreelanceCompany}
      />
    </div>
  );
};

export default UpdateSettings;
